<template>
  <ModalComponent
    :value="supportModalStore.openModal"
    @update:value="supportModalStore.setOpenModal"
    :hideClose="showForm !== 0"
  >
    <template #title>
      <h1
        class="gl_form-title"
        v-if="showForm === 0"
      >
        {{ t('components.support-chat-call.asisa-title') }}
      </h1>
    </template>
    <div v-show="showForm === 0">
      <SupportChatModalBody
        ref="$supportChatModalBody"
        :contactPhone="phoneNumber"
        @submit="submit"
      />

      <BaseButton
        type="transparent"
        @click="supportModalStore.setOpenModal(false)"
      >
        {{ t('footer.exit') }}
      </BaseButton>
    </div>
    <div
      class="ChatCallStatusService"
      v-show="showForm === 1"
    >
      <div class="header-status">
        <i class="icon-correcto" />

        <h1>
          {{ t('components.support-chat-call.success-thanks') }}
        </h1>

        <p>
          {{ t('components.support-chat-call.success-call') }}
        </p>
      </div>

      <BaseButton
        type="primary"
        @click="supportModalStore.setOpenModal(false), (showForm = 0)"
      >
        {{ t('components.support-chat-call.buttons.exit') }}
      </BaseButton>
    </div>
    <div
      class="ChatCallStatusService"
      v-show="showForm === 2"
    >
      <div class="header-status">
        <i class="icon-incorrecto" />

        <h1>
          {{ t('components.support-chat-call.error-first-problem') }}
        </h1>

        <p>
          {{ t('components.support-chat-call.error-first-repeat') }}
        </p>
      </div>
      <div class="buttons-footer">
        <BaseButton
          type="primary"
          @click="submit(false)"
        >
          {{ t('components.support-chat-call.buttons.repeat') }}
        </BaseButton>
        <BaseButton
          type="outlined"
          @click="showForm = 0"
          icon="icon-arrow-left"
        >
          {{ t('components.support-chat-call.buttons.back') }}
        </BaseButton>
      </div>
    </div>
    <div
      class="ChatCallStatusService"
      v-show="showForm === 3"
    >
      <div class="header-status">
        <i class="icon-incorrecto" />

        <h1>
          {{ t('components.support-chat-call.error-persist-again') }}
        </h1>

        <p>
          {{ t('components.support-chat-call.error-persist-repeat') }}
        </p>
        <div class="call-us">
          <div class="phone-text">
            {{ t('components.support-chat-call.free-phone-number-label') }}
          </div>
          <div class="phone-number">
            <a
              atphone
              :name="phoneNumberId"
              :id="phoneNumberId"
              :href="phoneNumberLink"
              @click="redirectToPhoneNumber"
              class="atphone"
            >
              {{ phoneNumber }}
            </a>
          </div>
        </div>
      </div>
      <BaseButton
        type="primary"
        @click="supportModalStore.setOpenModal(false), (showForm = 0)"
      >
        {{ t('components.support-chat-call.buttons.exit') }}
      </BaseButton>
    </div>
  </ModalComponent>
</template>

<script lang="ts" setup>
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import ModalComponent from '@/modules/core/components/utils/ModalComponent.vue'
  import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
  import SupportChatModalBody from '@/modules/common/common/components/supportChat/SupportChatModalBody.vue'
  import { useI18n } from 'vue-i18n'
  import { computed, ref } from 'vue'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useGTMStore } from '@/modules/core/stores/gtmStore'
  import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'
  import { PHONE_LINK } from '@/modules/core/models/IdHTMLItems'
  import { useBusinessStore } from '@/modules/core/stores/businessStore'
  import GTMEventType from '@/modules/core/models/GTMEventType'
  import UserTypesEnum from '@/modules/core/models/UserTypesEnum'

  const { t } = useI18n()
  const supportModalStore = useSupportModalStore()
  const commonStore = useCommonStore()
  const gtmStore = useGTMStore()
  const sharedGtmDataStore = useSharedGtmDataStore()
  const businessStore = useBusinessStore()
  const $supportChatModalBody = ref()
  const showForm = ref(0)
  const phoneNumber = computed(
    () => commonStore.getContactPhone() || t('components.support-chat-call.free-phone-number')
  )
  const phoneNumberLink = computed(() => 'tel:' + phoneNumber.value)
  const gtmPrefix = 'support_chat_call_component_'
  const phoneNumberId = computed(() => gtmPrefix + PHONE_LINK)

  async function redirectToPhoneNumber(e: Event) {
    e.preventDefault()
    gtmStore.sendEvent({
      event: GTMEventType.CLICK_PHONE_NUMBER,
      click_name: phoneNumberId.value,
      click_id: phoneNumberId.value,
      btn_label: phoneNumber.value,
      num_solicitud: sharedGtmDataStore.hiringNumber,
      producto: businessStore.getInsurance()?.productName ?? undefined,
      item_id: businessStore.getInsurance()?.productCode ?? undefined,
      ramo: sharedGtmDataStore.ramo,
      value: sharedGtmDataStore.policyValue,
      num_asegurados: sharedGtmDataStore.forWhomPeople,
      cobert_adicionales: sharedGtmDataStore.additionalCoverages ?? false,
      otra_comp: sharedGtmDataStore.anotherCompany,
      capital: sharedGtmDataStore.capital,
      tipo_beneficiario: sharedGtmDataStore.beneficiaries_type,
      num_beneficiarios: sharedGtmDataStore.beneficiaries_number,
      num_mascotas: sharedGtmDataStore.forWhomPets,
      tipo_viaje: sharedGtmDataStore.travel_type,
      destino_viaje: sharedGtmDataStore.travel_destination,
      duracion_viaje: sharedGtmDataStore.travel_duration
    })
    commonStore.getUserInfo().type = UserTypesEnum.QUALIFIED
    setTimeout(() => {
      const enlace = document.createElement('a')
      enlace.href = phoneNumberLink.value
      enlace.click()
    }, 200) // give time to gtm
  }

  async function submit(first: boolean) {
    const response = await $supportChatModalBody.value!.submit()
    if (response.ok) {
      showForm.value = 1
    } else {
      showForm.value = first ? 2 : 3
    }
  }
</script>

<style lang="scss" scoped>
  .gl_form-title {
    max-width: $size-375;
    font-size: $font-size-xl !important;
    font-weight: 700 !important;
    padding-bottom: 0 !important;
    padding-left: 0 !important;
    text-align: left !important;

    @include breakpoint-md {
      font-size: $font-size-2xl !important;
      padding: 0 3rem 0 0 !important;
      text-align: center !important;
    }
  }

  .ChatCallStatusService {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $gap-l;
    justify-content: space-between;
    position: relative;
    inset: 0;
    padding: $spacing-2xl $spacing-l;
    margin: 0 $spacing-negative-l $spacing-negative-s;
    background-color: $color-neutral-00;
    height: $size-full;
    z-index: $z-index-20;

    .header-status {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: $gap-l;
      justify-content: center;
    }

    .buttons-footer {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: $gap-l;
      justify-content: center;
      width: $size-full;
    }

    @include breakpoint-md {
      margin-top: $spacing-negative-s;
      margin-bottom: 0;
    }

    .request_number {
      text-align: center;
      padding: $spacing-xl 0 $spacing-l;

      span {
        background-color: $color-blue-200;
        color: $color-neutral-dark;
        padding: $spacing-s $spacing-xl;
        font-weight: $font-weight-700;
        border-radius: $b-radius-50;
      }
    }

    i {
      font-size: $font-size-60;
      width: $size-60;
      height: $size-60;

      @include breakpoint-md {
        font-size: $font-size-80;
        width: $size-80;
        height: $size-80;
      }

      &.icon-incorrecto {
        color: $color-error;
      }

      &.icon-correcto {
        color: $color-primary;
      }
    }

    h1 {
      color: $color-primary-dark;
      font-size: $font-size-2xl;
      font-weight: $font-weight-500;
      line-height: $line-height-130p;
      max-width: $size-400;
      text-align: center;

      @include breakpoint-md {
        font-size: $font-size-40;
      }
    }

    p {
      color: $color-primary-dark;
      max-width: $size-400;
      text-align: center;
      font-size: $font-size-l;
    }

    .call-us {
      border-top: solid 1px $color-neutral-light;
      border-bottom: solid 1px $color-neutral-light;
      padding: $spacing-xl 0;
      margin: $spacing-l 0;

      .hiring_number {
        color: $color-neutral-dark;
        text-align: center;
        margin: $spacing-negative-s 0 $spacing-l;
      }

      .phone-text {
        color: $color-primary-dark;
        font-size: $font-size-20;
        font-style: normal;
        font-weight: $font-weight-500;
        line-height: $line-height-130p;
        margin-bottom: $spacing-2xl;
      }

      .phone-number {
        text-align: center;

        a {
          font-size: $font-size-xl;
          font-style: normal;
          font-weight: $font-weight-700;
          line-height: $line-height-130p;
          color: $color-primary;
          text-decoration: none;
        }
      }
    }
  }
</style>
